import React, { useEffect, useState } from 'react';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { Box, FormControl, InputLabel, FormLabel, CircularProgress } from '@material-ui/core';
import { Alert } from '@mui/material';
import { styled } from '@material-ui/core/styles';
import CheckIcon from 'assets/icons/check-circle.svg';
import Img from 'retired/Img';
import FileUpload from 'retired/FileUpload';
import PicturesApi from 'entities/picture/api';
import { useDispatch } from 'react-redux';

/* 
  Similar to the old PictureSelector component which uses redux-form, but now using react-hook-form 
  @see organizer/app/retired/FormPictureSelector/index.jsx
*/

const PictureGrid = styled('ul')(() => ({
  padding: 0,
  margin: 0,
  listStyle: 'none',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '3px',
}));

const PictureTile = styled('li')(({ theme }) => ({
  display: 'inline-block',
  position: 'relative',
  overflow: 'hidden',
  marginRight: '1px',
  width: 'calc(25% - 8px)',
  paddingBottom: '14.0625%',
  [theme.breakpoints.only('md')]: {
    width: 'calc(33% - 1px)',
    paddingBottom: '18.5625%',
  },
  [theme.breakpoints.only('sm')]: {
    width: 'calc(50% - 1px)',
    paddingBottom: '28.125%',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    paddingBottom: '56.25%',
  },
}));

const PictureLink = styled('div')({
  position: 'absolute',
  width: '100%',
  height: '100%',
  cursor: 'pointer',
  '&:focus': {
    outline: 'none',
    '&:after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: '#000',
      opacity: 0.2,
      zIndex: 2,
    },
  },
  '&:hover': {
    '&:after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: '#000',
      opacity: 0.1,
      zIndex: 2,
    },
  },
});

const PictureLinkSelected = styled('div')({
  position: 'absolute',
  width: '100%',
  height: '100%',
  cursor: 'pointer',
  '&:after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#000',
    opacity: 0.2,
    zIndex: 2,
  },
  '&:focus': {
    outline: 'none',
  },
  '&:hover': {
    '&:after': {
      opacity: 0.3,
    },
  },
});

const CheckCircle = styled(Img)({
  position: 'absolute',
  top: '5px',
  right: '5px',
  zIndex: 3,
});

const PictureImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

interface Picture {
  id: string;
  resource: string;
  categories: string[];
}

interface FormPictureSelectorProps {
  control: Control<any>;
  name: string;
  labelText?: string;
  errors?: FieldErrors<any>;
  selectMultiple?: boolean;
  categories?: string[];
  enableFileUpload?: boolean;
  required?: boolean;
}

const FormPictureSelector: React.FC<FormPictureSelectorProps> = ({
  control,
  name,
  labelText = 'Select Picture',
  errors,
  selectMultiple = false,
  categories = [],
  enableFileUpload = true,
  required = false,
}) => {
  const dispatch = useDispatch();
  const [pictures, setPictures] = useState<Picture[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<string | null>(null);

  useEffect(() => {
    const loadPictures = async () => {
      try {
        setLoading(true);
        setError(null);

        const action = await PicturesApi.fetchAll(categories).toPromise();

        if (action && action.type) {
          dispatch(action);
        }

        const allPictures = action?.pictures ? Object.values(action.pictures.toJS()) : [];
        const transformedPictures = allPictures.map((picture: any) => ({
          id: picture.id,
          resource: picture.resource,
          categories: Array.isArray(picture.categories)
            ? picture.categories
            : picture.categories?.toArray?.() || [],
        }));

        setPictures(transformedPictures);
      } catch (err) {
        console.error('Error loading pictures:', err);
        setError('Failed to load pictures. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadPictures();
  }, [dispatch]);

  const handleFileUpload = (uploadedFileData: { publicUrl: string }) => {
    setUploadedFile(uploadedFileData.publicUrl);
  };

  const renderPictureGrid = (
    value: string | string[] | null,
    onChange: (value: string | string[] | null) => void
  ) => {
    const selectedPictures = Array.isArray(value) ? value : value ? [value] : [];

    const handlePictureClick = (resource: string) => {
      if (selectMultiple) {
        const isSelected = selectedPictures.includes(resource);
        if (isSelected) {
          // Don't allow deselection if required and this is the only selected item
          if (required && selectedPictures.length === 1) {
            return;
          }
          onChange(selectedPictures.filter((p) => p !== resource));
        } else {
          onChange([...selectedPictures, resource]);
        }
      } else {
        // For single selection, clicking the same picture deselects it (unless required)
        const isSelected = selectedPictures.includes(resource);
        if (isSelected) {
          // Don't allow deselection if required
          if (required) {
            return;
          }
          onChange(null);
        } else {
          onChange(resource);
        }
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent, resource: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handlePictureClick(resource);
      }
    };

    // Combine uploaded file with existing pictures
    const allPictures = [...pictures];
    if (uploadedFile && !pictures.some((p) => p.resource === uploadedFile)) {
      allPictures.unshift({
        id: 'uploaded',
        resource: uploadedFile,
        categories: [],
      });
    }

    return (
      <PictureGrid>
        {allPictures.map((picture) => {
          const isSelected = selectedPictures.includes(picture.resource);
          const LinkComponent = isSelected ? PictureLinkSelected : PictureLink;
          return (
            <PictureTile key={picture.id}>
              <LinkComponent
                role="button"
                tabIndex={0}
                onClick={() => handlePictureClick(picture.resource)}
                onKeyDown={(e) => handleKeyDown(e, picture.resource)}
              >
                <PictureImage
                  src={picture.resource.replace(
                    /https:\/\/(files|images).(.*\.?)(godeed.today|joindeed.org)\//g,
                    'https://$1.$2$3/c960x540/'
                  )}
                  alt=""
                />
                {isSelected && <CheckCircle src={CheckIcon} alt="" />}
              </LinkComponent>
            </PictureTile>
          );
        })}
        {enableFileUpload && (
          <PictureTile>
            <FileUpload
              onFinish={handleFileUpload}
              fixed
              name={name}
              formError={errors?.[name]?.message ? String(errors[name]?.message) : undefined}
            />
          </PictureTile>
        )}
      </PictureGrid>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <FormControl style={{ display: 'block' }}>
      <InputLabel shrink>
        {labelText}
        {required && <span> *</span>}
      </InputLabel>
      <br />
      <Controller
        name={name}
        control={control}
        rules={{ required: required ? 'A picture must be selected' : false }}
        render={({ field: { value, onChange } }) => renderPictureGrid(value, onChange)}
      />
      {!selectMultiple && required && (
        <FormLabel error={!!errors?.[name]} style={{ fontSize: 12, marginTop: 15 }}>
          {errors?.[name]?.message ? errors?.[name]?.message : 'A picture must be selected'}
        </FormLabel>
      )}
    </FormControl>
  );
};

export default FormPictureSelector;
